import React, { useState, useRef, useEffect } from 'react';
import { ImageMetadata, Image as Img } from "#/types/message";
import styles from './image-container.module.css';
import logger from '#/utils/logger';

const SHAPE_MAP = {
  "■": "square",
  "▲": "triangle-up",
  "▼": "triangle-down",
  "◆": "diamond",
  "●": "circle",
  "▭": "gradient",
} as const;

type Shape = typeof SHAPE_MAP[keyof typeof SHAPE_MAP] | "gradient" | null;

const VIRIDIS_WEIGHTED_STOPS: Array<[string, number]> = [
  ["#481467", 0],   // purple
  ["#3E4989", 15],
  ["#31688E", 24],
  ["#26828E", 35],
  ["#1F9E89", 48],  // teal
  ["#35B779", 59],  // green
  ["#6CCE59", 75],  // lighter green
  ["#b5de2b", 87],  // yellow-green
  ["#FDE725", 97], // yellow
];

const VIRIDIS_GRADIENT = `linear-gradient(125deg, ${
  VIRIDIS_WEIGHTED_STOPS.map(([c, p]) => `${c} ${p}%`).join(", ")
})`;

const isHexColor = (str: string) => /^#([0-9A-Fa-f]{3}){1,2}$/.test(str);

// Accept forms like: "▲#ff0000", "▼#0f0", "◆#123456"
function parseShapePrefix(prefix: string):
  { shape: Exclude<Shape, null> | null; color: string | "viridis" | null } {
  const p = (prefix || "").trim();

  // symbol + hex, e.g. "▲#ff0000"
  const m1 = /^([■▲▼◆●])\s*(#[0-9A-Fa-f]{3,6})$/.exec(p);
  if (m1) {
    const symbol = m1[1] as keyof typeof SHAPE_MAP;
    return { shape: SHAPE_MAP[symbol], color: m1[2] };
  }

  // gradient token: "viridis" or "▭viridis"
  if (/^(?:▭\s*)?viridis$/i.test(p)) {
    return { shape: "gradient", color: "viridis" };
  }

  // plain hex => treat as square (back-compat)
  if (isHexColor(p)) return { shape: "square", color: p };

  return { shape: null, color: null };
}

interface ImageContainerLayout {
  image: Img | undefined;
  onImageClick?: (image: Img, index: number, metadata: ImageMetadata | null) => void;
  maxDisplayWidth: number;
  maxDisplayHeight: number;
  internalActiveIndex: number;
  onHeightReady?: (height: number) => void;
  forceDefaultLayout?: boolean;
}

interface LegendItem {
  prefix: string;
  content: string;
}

type LayoutMode = 'default' | 'side-by-side';

const ImageContainer: React.FC<ImageContainerLayout> = ({
  image,
  onImageClick,
  maxDisplayWidth,
  maxDisplayHeight,
  internalActiveIndex,
  onHeightReady,
  forceDefaultLayout,
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const legendContainerRef = useRef<HTMLDivElement>(null);

  const lastNaturalDimsRef = useRef<{ width: number; height: number } | null>(null);

  const [legendItems, setLegendItems] = useState<LegendItem[]>([]);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [imageStyle, setImageStyle] = useState<React.CSSProperties>({});
  const [gridColumns, setGridColumns] = useState(2);
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('default');
  const [imageAreaWidth, setImageAreaWidth] = useState<number | null>(null);
  const [legendWidth, setLegendWidth] = useState<number | null>(null);

  const measureGenRef = useRef(0);
  const raf1Ref = useRef<number | null>(null);
  const raf2Ref = useRef<number | null>(null);
  const raf3Ref = useRef<number | null>(null);
  const ignoreRORef = useRef(false);
  const roRafRef = useRef<number | null>(null);

  const [contentWrapperStyle, setContentWrapperStyle] = useState<React.CSSProperties>({});

  const cancelRafs = () => {
    if (raf1Ref.current) cancelAnimationFrame(raf1Ref.current);
    if (raf2Ref.current) cancelAnimationFrame(raf2Ref.current);
    if (raf3Ref.current) cancelAnimationFrame(raf3Ref.current);
    raf1Ref.current = raf2Ref.current = raf3Ref.current = null;
  };

  // Clean up on unmount
  useEffect(() => () => cancelRafs(), []);

  function getUnconstrainedLegendWidth(): number {
    const container = legendContainerRef.current;
    if (!container) return 0;

    const clone = container.cloneNode(true) as HTMLElement;
    const original = container;

    // Copy computed styles
    const computed = getComputedStyle(original);
    for (const prop of [
      'font-size',
      'font-family',
      'font-weight',
      'letter-spacing',
      'line-height',
      'padding',
      'margin',
      'box-sizing',
    ]) {
      clone.style.setProperty(prop, computed.getPropertyValue(prop));
    }

    // Force the clone into a single-column layout for measurement,
    // overriding any existing multi-column style it may have cloned.
    clone.style.setProperty('--grid-columns', '1');

    // Layout + debug styles
    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.whiteSpace = 'nowrap';
    clone.style.width = 'auto';
    clone.style.height = 'auto';
    clone.style.maxWidth = 'none';
    clone.style.overflow = 'visible';
    clone.style.top = '0';
    clone.style.left = '0';
    clone.style.zIndex = '9999';
    clone.style.background = 'rgba(255,0,0,0.03)'; // debug: remove if unwanted

    document.body.appendChild(clone);
    const width = clone.scrollWidth;
    document.body.removeChild(clone);

    return width;
  }

  const calculateAllStyles = (
    naturalDimensions: { width: number; height: number } | null,
    mode: LayoutMode,
    onCalculationsComplete: () => void
  ) => {
    const myGen = ++measureGenRef.current;
    cancelRafs();
    ignoreRORef.current = true;

    const finish = (totalH: number) => {
      if (measureGenRef.current !== myGen) return;
      onHeightReady?.(totalH);
      onCalculationsComplete();
      ignoreRORef.current = false;
    };

    // PASS 1: Calculate and Set Final Styles
    raf1Ref.current = requestAnimationFrame(() => {
      if (measureGenRef.current !== myGen) return;

      const headerHeight = headerRef.current?.offsetHeight || 0;
      const imageMaxHeightBase = Math.max(0, maxDisplayHeight - headerHeight);

      let nextImageStyle: React.CSSProperties = {};
      let nextWrapperStyle: React.CSSProperties = {};
      let computedImgW = 0;

      if (naturalDimensions) {
        const ar = naturalDimensions.width / naturalDimensions.height;

        if (mode === 'side-by-side') {
          const gap = 8;
          const maxLegendFraction = 0.29;
          const legendContentWidth = getUnconstrainedLegendWidth();
          const maxLegendWidth = maxDisplayWidth * maxLegendFraction;
          const minLegendWidth = 90;

          // This calculation correctly clamps the width.
          let finalLegendWidth = Math.min(Math.max(legendContentWidth + 13, minLegendWidth), maxLegendWidth);
          setLegendWidth(finalLegendWidth);

          const availableImageWidth = maxDisplayWidth - finalLegendWidth - gap;

          let w = availableImageWidth;
          let h = w / ar;
          if (h > imageMaxHeightBase) {
            h = imageMaxHeightBase;
            w = h * ar;
          }
          computedImgW = w;

          nextImageStyle = {
            aspectRatio: `${naturalDimensions.width} / ${naturalDimensions.height}`,
            width: `${w}px`,
            height: 'auto',
            maxHeight: `${imageMaxHeightBase}px`,
          };

          nextWrapperStyle = { width: '100%' };

        } else { // 'default' mode
          let w = maxDisplayWidth;
          let h = w / ar;

          if (h > imageMaxHeightBase) {
            h = imageMaxHeightBase;
            w = h * ar;
          }
          computedImgW = w;

          nextWrapperStyle = { width: `${w}px`, margin: '0 auto' };

          nextImageStyle = {
            aspectRatio: `${naturalDimensions.width} / ${naturalDimensions.height}`,
            maxWidth: '100%',
            height: 'auto',
            maxHeight: `${imageMaxHeightBase}px`,
          };

          setLegendWidth(null);
        }
      } else {
        // Fallback for no image dimensions
        nextWrapperStyle = { width: `${maxDisplayWidth}px` };
        nextImageStyle = {
          maxWidth: '100%',
          height: `${imageMaxHeightBase}px`,
        };
      }

      setContentWrapperStyle(nextWrapperStyle);
      setImageStyle(nextImageStyle);
      setImageAreaWidth(computedImgW);

      // PASS 2: Measure the final result and report height
      raf3Ref.current = requestAnimationFrame(() => {
        if (measureGenRef.current !== myGen) return;

        const finalHeaderH = headerRef.current?.offsetHeight || 0;
        const finalImgH = imageRef.current?.getBoundingClientRect().height || 0;
        const finalLegendH = legendContainerRef.current?.getBoundingClientRect().height || 0;

        if (mode === 'side-by-side') {
          finish(finalHeaderH + Math.max(finalImgH, finalLegendH) + 8);
        } else {
          finish(finalHeaderH + finalImgH + finalLegendH + 8);
        }
      });
    });
  };

  useEffect(() => {
    // Reset state for the new image
    setIsImageLoaded(false);
    setLayoutMode('default'); // Always start with default

    const hasLegend = image?.metadata?.legend && image.metadata.legend.length > 0;

    // Parse legend items immediately
    if (image?.metadata?.legend && image.metadata.legend.length > 0) {
      const parsed = image.metadata.legend.split('\n').map(line => {
        let m = /^\s*([■▲▼◆●]?\s*#[0-9A-Fa-f]{3,6})\s+(.*)$/.exec(line);
        if (m) {
          const token = m[1].replace(/\s+/g, '');
          const text  = m[2].trim();
          return { prefix: token, content: text };
        }
        // 2) gradient token (viridis)
        m = /^\s*((?:▭\s*)?viridis)\s+(.*)$/i.exec(line);
        if (m) {
          return { prefix: m[1].replace(/\s+/g, ''), content: m[2].trim() };
        }
        return { prefix: '', content: line.trim() };
      });
      setLegendItems(parsed);
    } else {
      setLegendItems([]);
    }

    // Load the image to determine aspect ratio
    // Load the image to determine aspect ratio
    if (image?.file_name) {
      const img = new window.Image();
      img.onload = () => {
          const naturalDimensions = { width: img.naturalWidth, height: img.naturalHeight };
          lastNaturalDimsRef.current = naturalDimensions;
          const isPortrait = naturalDimensions.height / naturalDimensions.width > 0.75;

          const newMode: LayoutMode = forceDefaultLayout
              ? 'default'
              : (isPortrait && hasLegend ? 'side-by-side' : 'default');

          setLayoutMode(newMode);

          calculateAllStyles(naturalDimensions, newMode, () => setIsImageLoaded(true));

      };
      img.onerror = () => {
          logger.error("Failed to load image:", image.file_name);
          lastNaturalDimsRef.current = null;
          // Also update this call
          calculateAllStyles(null, 'default', () => setIsImageLoaded(true));
          // setIsImageLoaded(true); // Remove from here
      };
      img.src = image.file_name;
  } else {
      // If no image, finalize with default layout
      lastNaturalDimsRef.current = null;
      // And this one
      calculateAllStyles(null, 'default', () => setIsImageLoaded(true));
      // setIsImageLoaded(true); // Remove from here
  }
}, [image, maxDisplayWidth, maxDisplayHeight, forceDefaultLayout]);

  // Recalculate when legend grid columns change (legend reflow)
  useEffect(() => {
    if (!isImageLoaded) return;
    // Pass an empty function because we don't need to change the loaded state again.
    calculateAllStyles(lastNaturalDimsRef.current, layoutMode, () => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridColumns, layoutMode]);

  useEffect(() => {
    // This effect runs after the image has loaded and React has updated the DOM
    // with the new styles (including width and aspect-ratio).
    if (isImageLoaded && imageRef.current) {
      // Forcing a reflow by reading an element's property is a well-known trick
      // to make Safari (and other browsers) recalculate layout immediately.
      // This fixes rendering glitches where the image initially appears
      // stretched or in the wrong position.
      void imageRef.current.offsetHeight;
    }
  }, [isImageLoaded]);

  useEffect(() => {
    if (layoutMode === 'side-by-side') {
      setGridColumns(1);
      return;
    }
    const el = legendContainerRef.current;
    if (!el) return;

    const ro = new ResizeObserver(entries => {
      if (!entries[0]) return;
      if (ignoreRORef.current) return;
      const { width } = entries[0].contentRect;
      const next = width >= 750 ? 2 : 2;
      if (roRafRef.current) cancelAnimationFrame(roRafRef.current);
      roRafRef.current = requestAnimationFrame(() => {
        setGridColumns(cur => (cur !== next ? next : cur));
      });
    });

    ro.observe(el);
    return () => {
      if (roRafRef.current) cancelAnimationFrame(roRafRef.current);
      ro.disconnect();
    };
  }, [layoutMode, legendItems]);

  return (
    <div
      className={`${styles.layoutContainer} ${isImageLoaded ? styles.loaded : ''}`}
      ref={containerRef}
    >
      <div
        className={styles.imageBackground}
        onClick={() =>
          onImageClick && image && onImageClick(image, internalActiveIndex, image.metadata!)
        }
      >
        {/* Header */}
        {image?.metadata && (image.metadata.title || image.metadata.subtitle) && (
          <div ref={headerRef} className={styles.imageMetadataHeader}>
            {image.metadata.title && <h3 className={styles.imageTitle}>{image.metadata.title}</h3>}
            {image.metadata.subtitle && <p className={styles.imageSubtitle}>{image.metadata.subtitle}</p>}
          </div>
        )}

        {/* Default vs Side-by-side */}
        {layoutMode === 'default' ? (
          <>
            <div className={`${styles.imageArea} ${image?.metadata?.title ? styles.imageAreaWithHeader : ''}`}>
              {image && (
                <img
                  ref={imageRef}
                  className={styles.chatImage}
                  src={image.file_name}
                  alt="Image"
                  style={imageStyle}
                />
              )}
            </div>

            {legendItems.length > 0 && (
              <div ref={legendRef} className={styles.imageLegend}>
                <div
                  ref={legendContainerRef}
                  className={styles.legendGrid}
                  style={{ ['--grid-columns' as any]: gridColumns } as React.CSSProperties}
                >
                  {legendItems.map((item, index) => (
                    <LegendLine key={index} item={item} />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div
            className={`${styles.contentWrapper} ${styles.sideBySideLayout}`}
            style={contentWrapperStyle}
          >
            <div className={`${styles.imageArea} ${image?.metadata?.title ? styles.imageAreaWithHeader : ''}`} style={{ flexBasis: imageAreaWidth || 'auto' }}>
              {image && (
                <img
                  ref={imageRef}
                  className={styles.chatImage}
                  src={image.file_name}
                  alt="Image"
                  style={imageStyle}
                />
              )}
            </div>

            {legendItems.length > 0 && (
              <div
                ref={legendRef}
                className={styles.imageLegend}
                style={{ flexBasis: legendWidth || 'auto' }}
              >
                <div
                  ref={legendContainerRef}
                  className={styles.legendGrid}
                  style={{ ['--grid-columns' as any]: gridColumns } as React.CSSProperties}
                >
                  {legendItems.map((item, index) => (
                    <LegendLine key={index} item={item} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function LegendLine({ item }: { item: LegendItem }) {
  const { shape, color } = parseShapePrefix(item.prefix);
  return (
    <div className={styles.legendItem}>
      <div className={styles.legendItemInner}>
        {shape && color ? (
          <LegendSwatch shape={shape} color={color} />
        ) : isHexColor(item.prefix) ? (
          <span
            className={styles.legendColorBox}
            style={{ backgroundColor: item.prefix }}
            title={item.prefix}
          />
        ) : null}
        <span className={styles.legendLineContent}>
          {(() => {
            const m = item.content.match(/^(.*?)([.:])\s*(.*)$/);
            if (m) {
              const [, before, sep, after] = m;
              return (
                <>
                  <span className={styles.legendLinePrefix}>
                    {`${before.trim()}${sep}`}
                  </span>
                  {after && <span> {after.trim()}</span>}
                </>
              );
            }
            return item.content;
          })()}
        </span>
      </div>
    </div>
  );
}

function LegendSwatch({ shape, color }: { shape: Exclude<Shape, null>; color: string }) {
  const size = 14;
  const half = size / 2;
  const pad = 0;

  if (shape === "gradient" && color === "viridis") {
    return (
      <span
        className={styles.legendGradient}
        title="viridis"
        style={{
          background: VIRIDIS_GRADIENT,
          borderRadius: size,
          display: 'inline-block'
        }}
      />
    );
  }

  if (shape === "square") {
    return (
      <span
        className={styles.legendColorBox}
        style={{ backgroundColor: color, border: "1px solid black" }}
        title={color}
      />
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox={`-1 -1 ${size + 2} ${size + 2}`}
      aria-hidden="true"
      className={styles.legendColorBox}
      style={{ background: "transparent" }}
    >
      {shape === "triangle-up" && (
        <polygon
          points={`${half},${pad + 0.5} ${size - pad - 0.5},${size - pad - 0.5} ${pad + 0.5},${size - pad - 0.5}`}
          fill={color}
          stroke="black"
          strokeWidth={1}
        />
      )}
      {shape === "triangle-down" && (
        <polygon
          points={`${pad},${pad} ${size - pad},${pad} ${half},${size - pad}`}
          fill={color}
          stroke="black"
          strokeWidth={1}
        />
      )}
      {shape === "diamond" && (
        <polygon
          points={`${half},${pad} ${size - pad},${half} ${half},${size - pad} ${pad},${half}`}
          fill={color}
          stroke="black"
          strokeWidth={1}
        />
      )}
      {shape === "circle" && (
        <circle
          cx={half}
          cy={half}
          r={(size - 2) / 2}
          fill={color}
          stroke="black"
          strokeWidth={1}
        />
      )}
    </svg>
  );
}

export default ImageContainer;